import React, { useEffect, useRef, useState } from 'react';
import { T } from './orders.theme';
import { COPY } from './orders.copy';
import { STATUS_FILTERS } from './orders.selectors';
import { CheckIcon, ChevronDownIcon, PersonIcon, SearchIcon } from './OrdersIcons';

const DOT_COLOURS = {
  pending: T.pending,
  paid: T.paid,
  invoice: T.brand,
};

/** The four chips partition the list — every order sits under exactly one. */
function StatusChips({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {STATUS_FILTERS.map((filter) => {
        const active = filter.id === value;
        return (
          <button
            key={filter.id}
            type="button"
            onClick={() => onChange(filter.id)}
            style={{
              height: T.controlHeight,
              padding: '0 18px',
              borderRadius: T.radiusControl,
              background: active ? T.brand : T.surface,
              border: `1px solid ${active ? T.brand : T.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            {filter.dot && !active && (
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: T.radiusPill,
                  background: DOT_COLOURS[filter.dot],
                }}
              />
            )}
            <span
              style={{
                fontSize: 12.5,
                fontWeight: active ? 700 : 500,
                color: active ? T.surface : T.inkSoft,
              }}
            >
              {filter.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Waiter picker. The names come from the date-scoped set, so selecting one
 * never collapses the list to that single name.
 */
function StaffSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  if (!options.length) return null;

  const selected = options.find((o) => o.id === value);
  const rows = [{ id: null, name: COPY.allWaiters }, ...options];

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: T.controlHeight,
          minWidth: 158,
          padding: '0 11px',
          borderRadius: T.radiusControl,
          background: value ? T.selectedBg : T.surface,
          border: `1px solid ${value ? T.selectedBorder : T.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <PersonIcon size={15} color={value ? T.brandInk : T.inkSoft} width={1.6} />
        <span
          style={{
            flex: 1,
            fontSize: 12.5,
            fontWeight: 600,
            color: value ? T.brandInk : T.inkSoft,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {selected ? selected.name : COPY.allWaiters}
        </span>
        <ChevronDownIcon size={16} color={value ? T.brandInk : T.inkFaint} width={1.8} />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 40,
            width: 212,
            maxHeight: 280,
            overflowY: 'auto',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusPanel,
            boxShadow: T.popover,
            padding: 5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {rows.map((option) => {
            const active = option.id === value;
            return (
              <button
                key={option.id || 'all'}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                style={{
                  height: 36,
                  borderRadius: T.radiusSmall,
                  background: active ? T.selectedBg : 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 11px',
                  cursor: 'pointer',
                  font: 'inherit',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: active ? 700 : 500,
                    color: active ? T.brandInk : T.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {option.name}
                </span>
                {active && <CheckIcon size={15} color={T.brand} width={2} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function OrdersFilterBar({
  statusFilter,
  onStatusChange,
  staff,
  staffId,
  onStaffChange,
  search,
  onSearchChange,
  searchRef,
  showStaffFilter,
}) {
  return (
    <section
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <StatusChips value={statusFilter} onChange={onStatusChange} />

      <div style={{ display: 'flex', gap: 8 }}>
        {showStaffFilter && (
          <StaffSelect options={staff} value={staffId} onChange={onStaffChange} />
        )}

        <div
          style={{
            height: T.controlHeight,
            width: 224,
            padding: '0 11px',
            borderRadius: T.radiusControl,
            background: T.surface,
            border: `1px solid ${search ? T.borderStrong : T.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <SearchIcon size={15} color={T.inkFaint} />
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={COPY.searchPlaceholder}
            aria-label={COPY.searchPlaceholder}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 12.5,
              color: T.ink,
              font: 'inherit',
              padding: 0,
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: T.inkFaint,
                fontSize: 15,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
